import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { doc, getDoc, collection, getDocs, updateDoc, addDoc, serverTimestamp, query, where, orderBy, limit } from "firebase/firestore";
import { getNextMocNumber } from './firebaseUtils.js';
import Spinner from './Spinner.js';
import { ArrowLeft, Plus, Trash2, Search, X, ClipboardCheck, UserCheck, CreditCard, CheckCircle, Truck, Save, Send, Loader2, Copy, PackageSearch } from 'lucide-react';
import useClickOutside from './hooks/useClickOutside.js';
import { useConfig } from './hooks/useConfig.js';

const formatAddress = (addr) => {
  if (!addr) return '';
  if (typeof addr === 'string') return addr;
  return `${addr.attention ? addr.attention + ', ' : ''}${addr.address || ''}, ${addr.city || ''}, ${addr.state || ''} - ${addr.pincode || ''}`;
};

const CreateMOCPage = ({ db }) => {
  const { config } = useConfig();
  const navigate = useNavigate();
  const location = useLocation();
  const { mocId } = useParams();
  const initialOrderId = location.state?.orderId;

  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false); 
  const [bomDatabase, setBomDatabase] = useState([]);
  const [formData, setFormData] = useState({
    mocDate: new Date().toISOString().split('T')[0],
    mocNo: '',
    typeOfMOC: '', 
    documentType: 'Delivery',
    poNumber: '',
    poDate: '',
    customerName: '',
    shippingAddress: '',
    materialCode: '',
    productSummary: '',
    warehouse: '',
    aluminiumWeight: '',
    totalMeterNumber: '',
    components: [],
    orderId: '',
    orderNumber: '',
    firmId: '',
    preparedBy: '',
    quantityVerifiedBy: '',
    quantityVerifiedByDispatch: '',
    vehicleNumber: '',
    vehicleType: '',
    vehicleInTime: '',
    vehicleOutTime: '',
    vehicleDate: '',
    // Finance Fields
    paymentTerms: '',
    paymentStatus: '', 
    lastPaymentAmount: '',
    paymentRecDate: '',
    outstandingAmount: '',
    outstandingDays: '',
    financeRemarks: '',
    // Approval Fields
    approvedByFinance: '',
    approvedBySalesperson: '',
  });

  const [materialChecklistState, setMaterialChecklistState] = useState({
    stickers: false,
    laserMarking: false,
    materialWrapping: false,
    materialInstructions: ''
  });

  // Search state for adding components
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const searchRef = useClickOutside(() => setShowSuggestions(false));

  // Search state for linking orders
  const [orderSearchTerm, setOrderSearchTerm] = useState('');
  const [orderSuggestions, setOrderSuggestions] = useState([]);
  const [showOrderSuggestions, setShowOrderSuggestions] = useState(false);
  const orderSearchRef = useClickOutside(() => setShowOrderSuggestions(false));

  const [fulfillmentMap, setFulfillmentMap] = useState({});

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const bomSnap = await getDocs(collection(db, "bom_products"));
        const bomList = bomSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setBomDatabase(bomList);
        const weightMap = {};
        bomList.forEach(p => { weightMap[p.sku] = p.weight || 0; });

        if (mocId) {
          const mocDoc = await getDoc(doc(db, "dispatches", mocId));
          if (mocDoc.exists()) {
            const data = mocDoc.data();
            if (data.dispatchStatus === 'moc_pending_finance') {
              navigate(`/ops/moc/preview/${mocId}`);
              return;
            }
            
            // If editing, we still need to know the fulfillment of OTHER MOCs
            if (data.orderId) {
                await calculateFulfillment(data.orderId, mocId);
            }

            setFormData({ ...formData, ...data, id: mocDoc.id });
            if (data.materialChecklist) {
              setMaterialChecklistState(data.materialChecklist);
            }
          }
        } else if (initialOrderId) {
            await calculateFulfillment(initialOrderId);
            await handleOrderSelection(initialOrderId, weightMap);
        } else {
            // New independent MOC
            const generatedMocNo = await getNextMocNumber(db);
            setFormData(prev => ({ ...prev, mocNo: generatedMocNo }));
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [db, mocId, initialOrderId]);

  const calculateFulfillment = async (orderId, currentMocId = null) => {
      const q = query(collection(db, "dispatches"), where("orderId", "==", orderId));
      const snap = await getDocs(q);
      const map = {};
      snap.docs.forEach(doc => {
          if (doc.id === currentMocId) return; // Don't count quantities from the MOC we are currently editing
          const data = doc.data();
          if (data.dispatchStatus === 'packing_rejected') return; // Ignore rejected
          
          data.components?.forEach(c => {
              if (!map[c.sku]) map[c.sku] = { new: 0, rental: 0 };
              map[c.sku].new += (parseInt(c.newQty) || 0);
              map[c.sku].rental += (parseInt(c.rentalQty) || 0);
          });
      });
      setFulfillmentMap(map);
      return map;
  };

  const handleOrderSelection = async (orderId, weightMapOverride = null) => {
    try {
      const orderDoc = await getDoc(doc(db, "orders", orderId));
      if (orderDoc.exists()) {
        const orderData = orderDoc.data();
        const weightMap = weightMapOverride || bomDatabase.reduce((acc, p) => ({ ...acc, [p.sku]: p.weight || 0 }), {});
        
        // Refresh fulfillment map just in case
        const currentFulfillment = await calculateFulfillment(orderId);

        const initialComponents = [];
        const isRentalOrder = orderData.orderType === 'Rental';

        if (orderData.boms) {
          orderData.boms.forEach(bom => {
            bom.components.forEach(comp => {
              const existing = initialComponents.find(c => c.sku === comp.sku);
              const qty = Math.round(comp.baseQty * (bom.multiplier || 1));
              
              const prevNew = currentFulfillment[comp.sku]?.new || 0;
              const prevRental = currentFulfillment[comp.sku]?.rental || 0;
              const remainingQty = Math.max(0, qty - (isRentalOrder ? prevRental : prevNew));

              if (existing) {
                if (isRentalOrder) {
                    existing.orderQty += qty;
                    existing.rentalQty += remainingQty;
                } else {
                    existing.orderQty += qty;
                    existing.newQty += remainingQty;
                }
              } else {
                initialComponents.push({
                  id: comp.id || `${comp.sku}-${Date.now()}`,
                  sku: comp.sku,
                  name: comp.name,
                  orderQty: qty,
                  newQty: isRentalOrder ? 0 : remainingQty,
                  rentalQty: isRentalOrder ? remainingQty : 0,
                  weight: weightMap[comp.sku] || 0,
                  ticked: remainingQty > 0
                });
              }
            });
          });
        }

        let suggestedWarehouse = '';
        const rjFirms = ['nexrise_rj', 'cloud_rj', 'je'];
        const mhFirms = ['nexrise_mh', 'cloud_mh'];
        if (rjFirms.includes(orderData.firmId)) suggestedWarehouse = 'Jaipur';
        else if (mhFirms.includes(orderData.firmId)) suggestedWarehouse = 'Bhiwandi';

        const generatedMocNo = formData.mocNo || await getNextMocNumber(db);

        setFormData(prev => ({
          ...prev,
          mocNo: generatedMocNo,
          typeOfMOC: orderData.orderType || prev.typeOfMOC,
          poNumber: (orderData.orderType === 'Rental' ? orderData.workOrderNumber : orderData.poNumber) || '',
          poDate: (orderData.orderType === 'Rental' ? orderData.woDate : orderData.poDate) || '',
          customerName: orderData.customerName || '',
          shippingAddress: formatAddress(orderData.shippingAddress),
          productSummary: orderData.boms?.map(b => b.name).join(', ') || '',
          components: initialComponents,
          warehouse: suggestedWarehouse,
          orderId: orderId,
          orderNumber: orderData.orderNumber || '',
          firmId: orderData.firmId || '',
          paymentTerms: orderData.paymentTerms || '',
          paymentStatus: orderData.paymentStatus || '',
          lastPaymentAmount: orderData.lastPaymentAmount || '',
          paymentRecDate: orderData.paymentRecDate || '',
          outstandingAmount: orderData.outstandingAmount || '',
          outstandingDays: orderData.outstandingDays || '',
          financeRemarks: orderData.financeRemarks || '',
          approvedByFinance: orderData.verifiedBy || '', 
          approvedBySalesperson: orderData.salesPerson || '',
        }));

        if (orderData.materialChecklist) {
            setMaterialChecklistState(orderData.materialChecklist);
        }
        setOrderSearchTerm(orderData.orderNumber || '');
        setShowOrderSuggestions(false);
      }
    } catch (error) {
      console.error("Error selecting order:", error);
    }
  };

  const handleOrderSearch = async (val) => {
    setOrderSearchTerm(val);
    if (val.length > 1) {
        try {
            const q = query(
                collection(db, "orders"),
                where("status", "in", ["in_que", "confirmed_order", "moc_in_progress"]),
                limit(20)
            );
            const snap = await getDocs(q);
            const matches = snap.docs
                .map(d => ({ id: d.id, ...d.data() }))
                .filter(o => 
                    o.orderNumber?.toLowerCase().includes(val.toLowerCase()) || 
                    o.customerName?.toLowerCase().includes(val.toLowerCase())
                );
            setOrderSuggestions(matches);
            setShowOrderSuggestions(true);
        } catch (e) {
            console.error("Order search error:", e);
        }
    } else {
        setOrderSuggestions([]);
        setShowOrderSuggestions(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleComponentChange = (index, field, value) => {
    const updated = [...formData.components];
    const comp = updated[index];
    
    if (field === 'newQty' || field === 'rentalQty') {
        const val = parseInt(value) || 0;
        
        // Combined overshipping logic: (Sum of New/Rental in this MOC) + (Sum of New/Rental in other MOCs) <= Order Qty
        const prevMocdTotal = (fulfillmentMap[comp.sku]?.new || 0) + (fulfillmentMap[comp.sku]?.rental || 0);
        
        // Calculate what would be the new total for THIS specific row if we accept this value
        const currentNew = field === 'newQty' ? val : (comp.newQty || 0);
        const currentRental = field === 'rentalQty' ? val : (comp.rentalQty || 0);
        
        const totalWithThisChange = prevMocdTotal + currentNew + currentRental;
        const maxAllowedForThisField = Math.max(0, (comp.orderQty || 0) - prevMocdTotal - (field === 'newQty' ? (comp.rentalQty || 0) : (comp.newQty || 0)));

        if (totalWithThisChange > (comp.orderQty || 0)) {
            alert(`Combined Quantity (New + Rental) cannot exceed Order Quantity (${comp.orderQty}). Max allowed for this field: ${maxAllowedForThisField}`);
            updated[index][field] = maxAllowedForThisField;
        } else {
            updated[index][field] = val;
        }
    } else if (field === 'weight') {
        updated[index][field] = parseFloat(value) || 0;
    } else {
        updated[index][field] = value;
    }
    setFormData(prev => ({ ...prev, components: updated }));
  };

  const deleteComponent = (index) => {
    const updated = formData.components.filter((_, i) => i !== index);
    setFormData(prev => ({ ...prev, components: updated }));
  };

  const addComponent = (product) => {
    const existingIndex = formData.components.findIndex(c => c.sku === product.sku);
    if (existingIndex > -1) {
      const fieldToUpdate = formData.typeOfMOC === 'Rental' ? 'rentalQty' : 'newQty';
      handleComponentChange(existingIndex, fieldToUpdate, (formData.components[existingIndex][fieldToUpdate] || 0) + 1);
    } else {
      const isRental = formData.typeOfMOC === 'Rental';
      const newComp = {
        id: `${product.sku}-${Date.now()}`,
        sku: product.sku,
        name: product.name,
        newQty: isRental ? 0 : 1,
        rentalQty: isRental ? 1 : 0,
        weight: product.weight || 0,
        ticked: false
      };
      setFormData(prev => ({ ...prev, components: [...prev.components, newComp] }));
    }
    setSearchTerm('');
    setShowSuggestions(false);
  };

  const handleSearch = (val) => {
    setSearchTerm(val);
    if (val.length > 1) {
      const term = val.toLowerCase();
      const matches = bomDatabase.filter(p => 
        p.name.toLowerCase().includes(term) || 
        p.sku.toLowerCase().includes(term)
      ).slice(0, 10);
      setFilteredProducts(matches);
      setShowSuggestions(true);
    } else {
      setFilteredProducts([]);
      setShowSuggestions(false);
    }
  };

  const handleClone = () => {
      const clonedData = { 
          ...formData, 
          mocNo: '', 
          mocDate: new Date().toISOString().split('T')[0] 
      };
      navigate('/ops/moc/new', { state: { orderId: formData.orderId, initialData: clonedData } });
  };

  const totals = useMemo(() => {
    return formData.components.reduce((acc, c) => {
      if (c.ticked) {
        const rowQty = (parseInt(c.newQty) || 0) + (parseInt(c.rentalQty) || 0);
        acc.newQty += (parseInt(c.newQty) || 0);
        acc.rentalQty += (parseInt(c.rentalQty) || 0);
        acc.weight += rowQty * (parseFloat(c.weight) || 0);
      }
      return acc;
    }, { newQty: 0, rentalQty: 0, weight: 0 });
  }, [formData.components]);

  const saveMocDraft = async () => {
    if (!formData.mocNo) {
      alert("MOC Number is required to save a draft.");
      return;
    }
    setIsSaving(true);
    try {
      const payload = {
        ...formData,
        materialChecklist: materialChecklistState,
        totalWeight: totals.weight,
        dispatchStatus: 'moc_draft', 
        updatedAt: serverTimestamp(),
        createdAt: mocId ? formData.createdAt : serverTimestamp(),
      };

      if (mocId) {
        await updateDoc(doc(db, "dispatches", mocId), payload);
      } else {
        const docRef = await addDoc(collection(db, "dispatches"), payload);
        if (formData.orderId) {
          await updateDoc(doc(db, "orders", formData.orderId), { status: 'moc_in_progress' });
        }
      }
      alert("Draft Saved Successfully");
      navigate('/ops/shipments');
    } catch (error) {
      console.error("Error saving draft:", error);
      alert("Error: " + error.message);
    } finally {
      setIsSaving(false);
    }
  };

  const saveMocConfirm = async () => {
    if (!formData.mocNo) {
      alert("MOC Number is required to submit.");
      return;
    }
    if (!window.confirm("Are you sure you want to SUBMIT this MOC to Finance?")) return;
    
    setIsSaving(true);
    try {
      const payload = {
        ...formData,
        materialChecklist: materialChecklistState,
        totalWeight: totals.weight,
        dispatchStatus: 'moc_pending_finance', 
        updatedAt: serverTimestamp(),
        createdAt: mocId ? formData.createdAt : serverTimestamp(),
      };

      if (mocId) {
        await updateDoc(doc(db, "dispatches", mocId), payload);
      } else {
        await addDoc(collection(db, "dispatches"), payload);
      }

      // --- AUTO-CREATE DRAFT FOR REMAINING ---
      if (formData.orderId) {
          const remainingComponents = formData.components.map(c => {
              const prevMocd = (formData.typeOfMOC === 'Rental') 
                  ? (fulfillmentMap[c.sku]?.rental || 0) 
                  : (fulfillmentMap[c.sku]?.new || 0);
              
              const currentMocd = (formData.typeOfMOC === 'Rental') ? (c.rentalQty || 0) : (c.newQty || 0);
              const totalMocd = prevMocd + currentMocd;
              const balance = Math.max(0, (c.orderQty || 0) - totalMocd);

              if (balance > 0) {
                  return {
                      ...c,
                      newQty: formData.typeOfMOC === 'Rental' ? 0 : balance,
                      rentalQty: formData.typeOfMOC === 'Rental' ? balance : 0,
                      ticked: true
                  };
              }
              return null;
          }).filter(c => c !== null);

          if (remainingComponents.length > 0) {
              const nextMocNo = await getNextMocNumber(db);
              const draftPayload = {
                  ...formData,
                  mocNo: nextMocNo,
                  mocDate: new Date().toISOString().split('T')[0],
                  components: remainingComponents,
                  dispatchStatus: 'moc_draft',
                  updatedAt: serverTimestamp(),
                  createdAt: serverTimestamp(),
              };
              delete draftPayload.id;
              await addDoc(collection(db, "dispatches"), draftPayload);
              console.log("Auto-created draft MOC for balance:", nextMocNo);
          }

          await updateDoc(doc(db, "orders", formData.orderId), { status: 'pending_finance' });
      }

      alert("MOC Submitted to Finance Successfully. Draft for remaining balance created (if any).");
      navigate('/ops/shipments');
    } catch (error) {
      console.error("Error submitting MOC:", error);
      alert("Error: " + error.message);
    } finally {
      setIsSaving(false);
    }
  };

  const isSalesOrRental = formData.typeOfMOC === 'Sales' || formData.typeOfMOC === 'Rental';
  let poLabel = "PO Number";
  let dateLabel = "PO Date";

  if (formData.typeOfMOC === 'Rental') {
    poLabel = "WO Number";
    dateLabel = "WO Date";
  } else if (!isSalesOrRental) {
    poLabel = "Transfer Number";
    dateLabel = "Transfer Date";
  }

  if (loading) return <Spinner />;

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '20px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '25px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
          <button onClick={() => navigate(-1)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b' }}>
            <ArrowLeft size={24} />
          </button>
          <h2 style={{ margin: 0 }}>{mocId ? 'Edit' : 'Create'} Material Out Checklist</h2>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
            {isSaving && <Loader2 className="animate-spin" size={24} color="#2563eb" />}
            <button onClick={handleClone} style={secondaryBtnStyle} title="Clone to new draft"><Copy size={18}/> Clone</button>
            <button onClick={saveMocDraft} disabled={isSaving} style={draftBtnStyle}><Save size={18}/> Save Draft</button>
            <button onClick={saveMocConfirm} disabled={isSaving} style={confirmBtnStyle}><Send size={18}/> Submit to Finance</button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '30px' }}>
        <div style={cardStyle}>
          <h3 style={sectionHeaderStyle}>Document Details</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
            <div><label style={labelStyle}>Date</label><input type="date" name="mocDate" value={formData.mocDate} onChange={handleChange} style={inputStyle} /></div>
            <div>
              <label style={labelStyle}>Document No.</label>
              <input 
                type="text" 
                name="mocNo" 
                value={formData.mocNo || ''} 
                onChange={handleChange} 
                style={inputStyle} 
                placeholder={loading ? "Loading..." : "MOC Number"}
              />
            </div>
            <div>
                <label style={labelStyle}>Type of MOC</label>
                <select 
                    name="typeOfMOC" 
                    value={formData.typeOfMOC} 
                    onChange={handleChange} 
                    style={inputStyle}
                >
                    <option value="">Select Type...</option>
                    {config.moc_types.map(opt => (
                        <option key={opt} value={opt}>{opt}</option>
                    ))}
                </select>
            </div>
            <div>
              <label style={labelStyle}>Document Type</label>
              <div style={{ display: 'flex', gap: '15px', marginTop: '8px' }}>
                <label style={radioLabelStyle}><input type="radio" name="documentType" value="Delivery" checked={formData.documentType === 'Delivery'} onChange={handleChange} /> Delivery</label>
                <label style={radioLabelStyle}><input type="radio" name="documentType" value="Pickup" checked={formData.documentType === 'Pickup'} onChange={handleChange} /> Pickup</label>
              </div>
            </div>

            {isSalesOrRental && (
                <div style={{ gridColumn: 'span 2', position: 'relative' }} ref={orderSearchRef}>
                    <label style={labelStyle}>Link Order (Search by Number or Name)</label>
                    <div style={{ display: 'flex', alignItems: 'center', backgroundColor: '#fff', border: '1px solid #d1d5db', borderRadius: '6px', padding: '0 10px' }}>
                        <PackageSearch size={18} color="#64748b" />
                        <input 
                            type="text" 
                            placeholder="Find order to populate data..." 
                            value={orderSearchTerm} 
                            onChange={(e) => handleOrderSearch(e.target.value)} 
                            onFocus={() => orderSearchTerm.length > 1 && setShowOrderSuggestions(true)}
                            style={{ border: 'none', padding: '10px', outline: 'none', width: '100%', fontSize: '14px' }}
                        />
                        {formData.orderId && <CheckCircle size={18} color="#10b981" style={{ marginLeft: '10px' }} />}
                    </div>
                    {showOrderSuggestions && (
                        <div style={suggestionsBoxStyle}>
                            {orderSuggestions.map(o => (
                                <div key={o.id} onClick={() => handleOrderSelection(o.id)} style={suggestionItemStyle}>
                                    <div style={{ fontWeight: 'bold' }}>{o.orderNumber}</div>
                                    <div style={{ fontSize: '12px', color: '#64748b' }}>{o.customerName} - {o.subject}</div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            <div><label style={labelStyle}>{poLabel}</label><input type="text" name="poNumber" value={formData.poNumber} onChange={handleChange} style={inputStyle} /></div>
            <div><label style={labelStyle}>{dateLabel}</label><input type="date" name="poDate" value={formData.poDate} onChange={handleChange} style={inputStyle} /></div>
            
            <div style={{ gridColumn: 'span 2' }}>
                <label style={labelStyle}>Salesperson {isSalesOrRental ? '(Read-Only)' : ''}</label>
                <input 
                    type="text" 
                    name="approvedBySalesperson"
                    value={formData.approvedBySalesperson} 
                    onChange={handleChange}
                    style={{ ...inputStyle, backgroundColor: isSalesOrRental ? '#f8fafc' : '#fff' }} 
                    disabled={isSalesOrRental}
                    placeholder="Enter salesperson name..."
                />
            </div>
          </div>
        </div>

        <div style={cardStyle}>
          <h3 style={sectionHeaderStyle}>Customer & Location</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
            <div><label style={labelStyle}>Customer Name</label><input type="text" name="customerName" value={formData.customerName} onChange={handleChange} style={inputStyle} /></div>
            <div><label style={labelStyle}>Shipping Address</label><textarea name="shippingAddress" value={formData.shippingAddress} onChange={handleChange} style={{ ...inputStyle, minHeight: '60px' }} /></div>
            <div style={{ gridTemplateColumns: '1fr 1fr', display: 'grid', gap: '15px' }}>
              <div><label style={labelStyle}>Material Code</label><input type="text" name="materialCode" value={formData.materialCode} onChange={handleChange} style={inputStyle} /></div>
              <div><label style={labelStyle}>Warehouse</label>
                <select name="warehouse" value={formData.warehouse} onChange={handleChange} style={inputStyle}>
                  <option value="">Select...</option>
                  {config.warehouses.map(w => <option key={w} value={w}>{w}</option>)}
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div style={cardStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1 }}>
            <h3 style={{ margin: 0, whiteSpace: 'nowrap' }}>BOM:</h3>
            <input 
                type="text" 
                name="productSummary" 
                value={formData.productSummary} 
                onChange={handleChange} 
                placeholder="BOM Description from order..."
                style={{ 
                    fontSize: '16px', 
                    fontWeight: 'bold', 
                    color: '#1e293b', 
                    border: 'none', 
                    borderBottom: '1px dashed #cbd5e1', 
                    width: '100%', 
                    padding: '4px 0',
                    outline: 'none'
                }} 
            />
          </div>
          
          <div style={{ position: 'relative', width: '300px', marginLeft: '20px' }} ref={searchRef}>
            <div style={{ display: 'flex', alignItems: 'center', backgroundColor: '#f3f4f6', padding: '8px 12px', borderRadius: '6px' }}>
              <Search size={16} color="#64748b" />
              <input 
                type="text" 
                placeholder="Search components to add..." 
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                onFocus={() => searchTerm.length > 1 && setShowSuggestions(true)}
                style={{ border: 'none', background: 'transparent', marginLeft: '8px', outline: 'none', width: '100%', fontSize: '13px' }}
              />
            </div>
            {showSuggestions && (
              <div style={suggestionsBoxStyle}>
                {filteredProducts.map(p => (
                  <div key={p.id} onClick={() => addComponent(p)} style={suggestionItemStyle}>
                    <strong>{p.sku}</strong> - {p.name}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table style={{ ...tableStyle, tableLayout: 'fixed' }}>
            <thead>
              <tr style={{ backgroundColor: '#f9fafb' }}>
                <th style={{ ...thStyle, width: '50px' }}>S.No.</th>
                <th style={{ ...thStyle, width: '100px' }}>SKU</th>
                <th style={{ ...thStyle, textAlign: 'left' }}>Component Name</th>
                <th style={{ ...thStyle, width: '80px' }}>Order Qty</th>
                <th style={{ ...thStyle, width: '80px' }}>New</th>
                <th style={{ ...thStyle, width: '80px' }}>Rental</th>
                <th style={{ ...thStyle, width: '50px' }}>Tick</th>
                <th style={{ ...thStyle, width: '100px' }}>Weight</th>
                <th style={{ ...thStyle, width: '100px' }}>Total Weight</th>
                <th style={{ ...thStyle, width: '60px' }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {formData.components.map((c, index) => {
                const totalRowWeight = ( (parseInt(c.newQty || 0) + parseInt(c.rentalQty || 0)) * (parseFloat(c.weight) || 0) ).toFixed(2);
                
                return (
                  <tr key={c.id || index} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={tdStyle}>{index + 1}</td>
                    <td style={tdStyle}>{c.sku}</td>
                    <td style={{ ...tdStyle, textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.name}</td>
                    <td style={{ ...tdStyle, fontWeight: 'bold', color: '#64748b' }}>{c.orderQty || 0}</td>
                    <td style={tdStyle}>
                        <input 
                          type="number" 
                          value={c.newQty} 
                          onChange={(e) => handleComponentChange(index, 'newQty', e.target.value)} 
                          style={{ ...inputStyle, width: '60px', textAlign: 'center', padding: '5px' }}
                        />
                    </td>
                    <td style={tdStyle}>
                        <input 
                          type="number" 
                          value={c.rentalQty} 
                          onChange={(e) => handleComponentChange(index, 'rentalQty', e.target.value)} 
                          style={{ ...inputStyle, width: '60px', textAlign: 'center', padding: '5px' }}
                        />
                    </td>
                    <td style={tdStyle}>
                      <input 
                        type="checkbox" 
                        checked={c.ticked} 
                        onChange={(e) => handleComponentChange(index, 'ticked', e.target.checked)} 
                      />
                    </td>
                    <td style={tdStyle}>
                        <input 
                          type="number" 
                          step="0.01"
                          value={c.weight} 
                          onChange={(e) => handleComponentChange(index, 'weight', e.target.value)} 
                          style={{ ...inputStyle, width: '70px', textAlign: 'center', padding: '5px' }}
                        />
                    </td>
                    <td style={tdStyle}>{totalRowWeight}</td>
                    <td style={tdStyle}>
                      <button onClick={() => deleteComponent(index)} style={{ border: 'none', background: 'none', color: '#ef4444', cursor: 'pointer' }}>
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                );
              })}
              <tr style={{ backgroundColor: '#f9fafb', fontWeight: 'bold' }}>
                <td colSpan="4" style={{ ...tdStyle, textAlign: 'right' }}>TOTAL SUMMARY</td>
                <td style={tdStyle}>{totals.newQty}</td>
                <td style={tdStyle}>{totals.rentalQty}</td>
                <td></td>
                <td></td>
                <td style={tdStyle}>{totals.weight.toFixed(2)} kg</td>
                <td></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginTop: '20px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            <div style={cardStyle}>
                <h3 style={sectionHeaderStyle}><ClipboardCheck size={18} /> Material Checklist</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '15px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', fontSize: '14px', color: '#374151', cursor: 'pointer' }}>
                        <input type="checkbox" checked={materialChecklistState.stickers} onChange={(e) => setMaterialChecklistState({...materialChecklistState, stickers: e.target.checked})} style={{ marginRight: '10px' }} />
                        Stickers
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', fontSize: '14px', color: '#374151', cursor: 'pointer' }}>
                        <input type="checkbox" checked={materialChecklistState.laserMarking} onChange={(e) => setMaterialChecklistState({...materialChecklistState, laserMarking: e.target.checked})} style={{ marginRight: '10px' }} />
                        Laser Marking
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', fontSize: '14px', color: '#374151', cursor: 'pointer' }}>
                        <input type="checkbox" checked={materialChecklistState.materialWrapping} onChange={(e) => setMaterialChecklistState({...materialChecklistState, materialWrapping: e.target.checked})} style={{ marginRight: '10px' }} />
                        Material Wrapping
                    </label>
                </div>
                <textarea 
                    name="materialInstructions" 
                    value={materialChecklistState.materialInstructions} 
                    onChange={(e) => setMaterialChecklistState({...materialChecklistState, materialInstructions: e.target.value})} 
                    placeholder="Please add any additional instructions here" 
                    style={{...inputStyle, minHeight: '80px', resize: 'vertical'}} 
                />
            </div>

            <div style={cardStyle}>
                <h3 style={sectionHeaderStyle}><Truck size={18} /> Physical Summary</h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                    <div>
                        <label style={labelStyle}>Aluminium Weight (kg)</label>
                        <input type="number" name="aluminiumWeight" value={formData.aluminiumWeight} onChange={handleChange} style={inputStyle} />
                    </div>
                    <div>
                        <label style={labelStyle}>Total Meter Number</label>
                        <input type="number" step="0.01" name="totalMeterNumber" value={formData.totalMeterNumber} onChange={handleChange} style={inputStyle} />
                    </div>
                </div>
            </div>
        </div>

        <div style={cardStyle}>
            <h3 style={sectionHeaderStyle}><UserCheck size={18} /> Final Verification</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                <div style={{ gridColumn: 'span 2' }}>
                    <label style={labelStyle}>Prepared By</label>
                    <select 
                        name="preparedBy" 
                        value={formData.preparedBy} 
                        onChange={handleChange} 
                        style={inputStyle}
                    >
                        <option value="">Select Staff...</option>
                        {config.moc_prepared_by.map(opt => (
                            <option key={opt} value={opt}>{opt}</option>
                        ))}
                    </select>
                </div>
                <div>
                    <label style={labelStyle}>Prior to Approval</label>
                    <select 
                        name="quantityVerifiedBy" 
                        value={formData.quantityVerifiedBy} 
                        onChange={handleChange} 
                        style={inputStyle}
                    >
                        <option value="">Select...</option>
                        {config.moc_verified_by.map(opt => (
                            <option key={opt} value={opt}>{opt}</option>
                        ))}
                    </select>
                </div>
                <div>
                    <label style={labelStyle}>Prior to Dispatch</label>
                    <input 
                        type="text" 
                        name="quantityVerifiedByDispatch" 
                        value={formData.quantityVerifiedByDispatch} 
                        onChange={handleChange} 
                        placeholder="Verified By..." 
                        style={{ ...inputStyle, backgroundColor: '#f8fafc' }} 
                        disabled
                    />
                </div>
                <div style={{ gridColumn: 'span 2', marginTop: '10px', paddingTop: '10px', borderTop: '1px solid #f3f4f6' }}>
                    <label style={labelStyle}>Vehicle Details</label>
                </div>
                <div>
                    <label style={labelStyle}>Vehicle Number</label>
                    <input 
                        type="text" 
                        name="vehicleNumber" 
                        value={formData.vehicleNumber} 
                        onChange={handleChange} 
                        placeholder="e.g. RJ-14-GA-1234" 
                        style={{ ...inputStyle, backgroundColor: '#f8fafc' }} 
                        disabled
                    />
                </div>
                <div>
                    <label style={labelStyle}>Vehicle Type</label>
                    <input 
                        type="text" 
                        name="vehicleType" 
                        value={formData.vehicleType} 
                        onChange={handleChange} 
                        style={{ ...inputStyle, backgroundColor: '#f8fafc' }} 
                        disabled
                    />
                </div>
                <div>
                    <label style={labelStyle}>Vehicle Date</label>
                    <input 
                        type="date" 
                        name="vehicleDate" 
                        value={formData.vehicleDate} 
                        onChange={handleChange} 
                        style={{ ...inputStyle, backgroundColor: '#f8fafc' }} 
                        disabled
                    />
                </div>
                <div>
                    <label style={labelStyle}>Vehicle In Time</label>
                    <input 
                        type="time" 
                        name="vehicleInTime" 
                        value={formData.vehicleInTime} 
                        onChange={handleChange} 
                        style={{ ...inputStyle, backgroundColor: '#f8fafc' }} 
                        disabled
                    />
                </div>
                <div>
                    <label style={labelStyle}>Vehicle Out Time</label>
                    <input 
                        type="time" 
                        name="vehicleOutTime" 
                        value={formData.vehicleOutTime} 
                        onChange={handleChange} 
                        style={{ ...inputStyle, backgroundColor: '#f8fafc' }} 
                        disabled
                    />
                </div>
            </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '20px', marginTop: '20px' }}>
        <div style={cardStyle}>
            <h3 style={sectionHeaderStyle}><CreditCard size={18} /> Finance Details</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                <div style={{ gridColumn: 'span 2' }}>
                    <label style={labelStyle}>Payment Terms</label>
                    <input type="text" name="paymentTerms" value={formData.paymentTerms} style={{...inputStyle, backgroundColor: '#f8fafc'}} disabled />
                </div>
                <div style={{ gridColumn: 'span 2' }}>
                    <label style={labelStyle}>Payment Status</label>
                    <div style={{ display: 'flex', gap: '15px', marginTop: '8px' }}>
                        {['Received', 'Credit', 'Old Payment Received'].map(status => (
                            <label key={status} style={{...radioLabelStyle, opacity: 0.7}}>
                                <input 
                                    type="radio" 
                                    name="paymentStatus" 
                                    value={status} 
                                    checked={formData.paymentStatus === status} 
                                    disabled
                                /> {status}
                            </label>
                        ))}
                    </div>
                </div>
                <div>
                    <label style={labelStyle}>Last Payment Amount</label>
                    <input type="number" name="lastPaymentAmount" value={formData.lastPaymentAmount} style={{...inputStyle, backgroundColor: '#f8fafc'}} disabled />
                </div>
                <div>
                    <label style={labelStyle}>Payment Rec. Date</label>
                    <input type="date" name="paymentRecDate" value={formData.paymentRecDate} style={{...inputStyle, backgroundColor: '#f8fafc'}} disabled />
                </div>
                <div>
                    <label style={labelStyle}>Outstanding Amount</label>
                    <input type="number" name="outstandingAmount" value={formData.outstandingAmount} style={{...inputStyle, backgroundColor: '#f8fafc'}} disabled />
                </div>
                <div>
                    <label style={labelStyle}>Outstanding Days</label>
                    <input type="number" name="outstandingDays" value={formData.outstandingDays} style={{...inputStyle, backgroundColor: '#f8fafc'}} disabled />
                </div>
                <div style={{ gridColumn: 'span 2' }}>
                    <label style={labelStyle}>Finance Remarks (Read-Only)</label>
                    <textarea 
                        name="financeRemarks" 
                        value={formData.financeRemarks} 
                        style={{ ...inputStyle, minHeight: '60px', backgroundColor: '#f8fafc' }} 
                        disabled 
                    />
                </div>
            </div>
        </div>

        <div style={cardStyle}>
            <h3 style={sectionHeaderStyle}><CheckCircle size={18} /> Approvals</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                <div>
                    <label style={labelStyle}>Finance Verified By</label>
                    <input 
                        type="text" 
                        value={formData.approvedByFinance} 
                        style={{...inputStyle, backgroundColor: '#f8fafc'}} 
                        disabled
                    />
                </div>
                <div>
                    <label style={labelStyle}>Salesperson</label>
                    <input 
                        type="text" 
                        value={formData.approvedBySalesperson} 
                        style={{...inputStyle, backgroundColor: isSalesOrRental ? '#f8fafc' : '#fff'}} 
                        disabled={isSalesOrRental}
                        placeholder="Salesperson name..."
                    />
                </div>
            </div>
        </div>
      </div>
    </div>
  );
};

const cardStyle = { backgroundColor: 'white', padding: '20px', borderRadius: '10px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' };
const sectionHeaderStyle = { fontSize: '16px', fontWeight: 'bold', borderBottom: '1px solid #f3f4f6', paddingBottom: '10px', marginBottom: '15px', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '8px' };
const labelStyle = { display: 'block', fontSize: '11px', fontWeight: '700', color: '#64748b', marginBottom: '6px', textTransform: 'uppercase' };
const inputStyle = { width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '14px', boxSizing: 'border-box' };
const radioLabelStyle = { display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', cursor: 'pointer' };
const saveBtnStyle = { backgroundColor: '#2563eb', color: 'white', border: 'none', padding: '10px 25px', borderRadius: '6px', fontWeight: 'bold', cursor: 'pointer' };
const draftBtnStyle = { ...saveBtnStyle, backgroundColor: 'white', color: '#374151', border: '1px solid #d1d5db', display: 'flex', alignItems: 'center', gap: '8px' };
const confirmBtnStyle = { ...saveBtnStyle, backgroundColor: '#059669', display: 'flex', alignItems: 'center', gap: '8px' };
const secondaryBtnStyle = { ...saveBtnStyle, backgroundColor: 'white', color: '#64748b', border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 15px', fontSize: '13px' };
const tableStyle = { width: '100%', borderCollapse: 'collapse', marginTop: '10px' };
const thStyle = { padding: '12px 10px', fontSize: '11px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', borderBottom: '2px solid #f3f4f6' };
const tdStyle = { padding: '12px 10px', fontSize: '13px', color: '#374151', textAlign: 'center' };
const suggestionsBoxStyle = { position: 'absolute', top: '100%', left: 0, width: '100%', backgroundColor: 'white', border: '1px solid #d1d5db', borderRadius: '6px', marginTop: '4px', zIndex: 10, boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' };
const suggestionItemStyle = { padding: '10px 12px', cursor: 'pointer', borderBottom: '1px solid #f3f4f6', fontSize: '13px' };

export default CreateMOCPage;
